import React, { useState } from 'react';
import { Button, Card, Input } from '../common';

const MAX_CONTENT_CHARS = 500_000;
const MIN_CONTENT_CHARS = 1;
const MAX_TITLE_CHARS = 500;

interface TextInputFormProps {
  onSubmit: (content: string, title?: string) => Promise<void>;
  creditCost?: number;
  disabled?: boolean;
}

export default function TextInputForm({
  onSubmit,
  creditCost = 1,
  disabled = false,
}: TextInputFormProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedLength = content.trim().length;
  const remaining = MAX_CONTENT_CHARS - content.length;
  const overLimit = content.length > MAX_CONTENT_CHARS;

  const validate = (value: string): string | null => {
    if (value.trim().length < MIN_CONTENT_CHARS) {
      return 'Paste some text to convert.';
    }
    if (value.length > MAX_CONTENT_CHARS) {
      return `Content is too long (${value.length.toLocaleString()} / ${MAX_CONTENT_CHARS.toLocaleString()} characters).`;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate(content);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const trimmedTitle = title.trim();
      await onSubmit(content, trimmedTitle ? trimmedTitle : undefined);
      setTitle('');
      setContent('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add source');
    } finally {
      setLoading(false);
    }
  };

  const submitDisabled = disabled || loading || trimmedLength === 0 || overLimit;

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            label="Title (optional)"
            type="text"
            placeholder="Leave blank to use the first line of your text"
            value={title}
            maxLength={MAX_TITLE_CHARS}
            onChange={(e) => setTitle(e.target.value)}
            disabled={disabled || loading}
          />
        </div>

        <div>
          <label
            htmlFor="paste-text-content"
            className="block font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground mb-2"
          >
            Your text
          </label>
          <textarea
            id="paste-text-content"
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Paste an article, essay, or notes. noctua reads it aloud and turns it into audio."
            rows={8}
            disabled={disabled || loading}
            className="w-full font-body text-sm bg-background border border-border rounded-sm px-3 py-2 resize-y min-h-[8rem] focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60"
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby="paste-text-counter paste-text-error"
          />
          <div className="flex items-center justify-between mt-2">
            <p
              id="paste-text-error"
              className="font-body text-sm text-destructive min-h-[1.25rem]"
              role={error ? 'alert' : undefined}
            >
              {error ?? ''}
            </p>
            <p
              id="paste-text-counter"
              className={`font-mono text-xs tracking-wide uppercase ${
                overLimit ? 'text-destructive' : 'text-muted-foreground'
              }`}
            >
              {content.length.toLocaleString()} / {MAX_CONTENT_CHARS.toLocaleString()}
              {overLimit && (
                <>
                  {' · '}
                  <span>{Math.abs(remaining).toLocaleString()} over</span>
                </>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <p className="font-mono text-xs tracking-wide uppercase text-muted-foreground">
            Uses <span className="text-foreground">{creditCost} credit</span>
          </p>
          <Button type="submit" loading={loading} disabled={submitDisabled}>
            Convert to audio
          </Button>
        </div>
      </form>
    </Card>
  );
}
